import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X, ChevronDown } from "lucide-react";
import { useApp } from "../../context/AppContext";

// Main nav — clean Italian boutique
const primaryNav = [
    { label: "Nuovi Arrivi", to: "/collection/nuovi-arrivi" },
    { label: "Neonato", to: "/collection/neonato" },
    { label: "Bambino", to: "/collection/bambino" },
    { label: "Bambina", to: "/collection/bambina" },
    { label: "Ragazzo", to: "/collection/ragazzo" },
    { label: "Ragazza", to: "/collection/ragazza" },
    { label: "Completi", to: "/collection/completi" },
    { label: "Scarpe", to: "/collection/calzature" },
    { label: "Accessori", to: "/collection/accessori" },
    { label: "Outlet", to: "/collection/outlet" },
];

export default function Navbar() {
    const { homepage, cartCount, setCartOpen, user } = useApp();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const onSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-40 bg-[color:var(--ms-ivory)] border-b border-[color:var(--ms-border)]" data-testid="site-navbar">
            {/* Top thin banner */}
            <div className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] text-[11px] tracking-[0.18em] uppercase py-1.5 px-6 text-center font-medium">
                Spedizione gratuita in Italia sopra 79 €
            </div>

            <div className="px-5 md:px-10 lg:px-16 pt-4 pb-3 md:pt-6 md:pb-4 grid grid-cols-[auto_1fr_auto] items-center gap-4">
                {/* Left: mobile menu + search on desktop */}
                <div className="flex items-center gap-4">
                    <button
                        className="md:hidden text-[color:var(--ms-charcoal)]"
                        onClick={() => setOpen(!open)}
                        aria-label="menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                    <button onClick={() => setSearchOpen(!searchOpen)} aria-label="cerca" className="hidden md:inline-flex text-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-sage-dark)] transition-colors" data-testid="search-toggle">
                        <Search size={18} />
                    </button>
                </div>

                {/* Center: logo — tall detailed watercolour illustration + wordmark; keep proportions */}
                <Link to="/" className="justify-self-center flex items-center justify-center py-1" data-testid="logo-link" aria-label="Mini Style — home">
                    {homepage?.logo_image_url ? (
                        <img
                            src={homepage.logo_image_url}
                            alt="Mini Style — abbigliamento e calzature da 0/16 anni"
                            className="h-20 sm:h-24 md:h-32 lg:h-36 w-auto object-contain select-none"
                            style={{ mixBlendMode: "multiply" }}
                            draggable="false"
                        />
                    ) : (
                        <div className="text-center">
                            <div className="font-display font-italic-display text-2xl md:text-3xl text-[color:var(--ms-charcoal)] leading-none">{homepage?.logo_text || "Mini Style"}</div>
                            {homepage?.logo_tagline && (
                                <div className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[color:var(--ms-muted)] mt-1 font-medium">{homepage.logo_tagline}</div>
                            )}
                        </div>
                    )}
                </Link>

                {/* Right: icons */}
                <div className="flex items-center gap-4 text-[color:var(--ms-charcoal)]">
                    <button onClick={() => setSearchOpen(!searchOpen)} aria-label="cerca" className="md:hidden hover:text-[color:var(--ms-sage-dark)]">
                        <Search size={18} />
                    </button>
                    <Link to={user ? "/account" : "/login"} aria-label="account" className="hover:text-[color:var(--ms-sage-dark)] transition-colors" data-testid="account-link">
                        <User size={18} />
                    </Link>
                    <Link to="/wishlist" aria-label="preferiti" className="hover:text-[color:var(--ms-sage-dark)] transition-colors hidden sm:inline-block" data-testid="wishlist-link">
                        <Heart size={18} />
                    </Link>
                    <button onClick={() => setCartOpen(true)} aria-label="carrello" className="relative hover:text-[color:var(--ms-sage-dark)] transition-colors" data-testid="cart-toggle">
                        <ShoppingBag size={18} />
                        {cartCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-[color:var(--ms-sage)] text-[color:var(--ms-warm-white)] text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* Desktop primary nav */}
            <nav className="hidden md:block border-t border-[color:var(--ms-border)]">
                <ul className="flex justify-center items-center gap-7 lg:gap-9 px-6 py-3.5 text-[12px] tracking-[0.16em] uppercase text-[color:var(--ms-charcoal)] font-semibold">
                    {primaryNav.map(l => (
                        <li key={l.to}>
                            <Link
                                to={l.to}
                                className={`link-underline hover:text-[color:var(--ms-sage-dark)] transition-colors ${l.label === "Outlet" ? "text-[color:var(--ms-terracotta)]" : ""}`}
                                data-testid={`nav-${l.label.toLowerCase().replace(/ /g, '-')}`}
                            >
                                {l.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Search bar */}
            {searchOpen && (
                <form onSubmit={onSearch} className="border-t border-[color:var(--ms-border)] px-6 md:px-12 lg:px-16 py-4 flex gap-3 items-center bg-[color:var(--ms-warm-white)]" data-testid="search-bar">
                    <Search size={16} className="text-[color:var(--ms-sage-dark)]" />
                    <input
                        autoFocus
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Cerca capi, marchi, colori…"
                        className="bg-transparent border-none outline-none flex-1 text-[color:var(--ms-charcoal)] placeholder:text-[color:var(--ms-muted)] text-sm"
                        data-testid="search-input"
                    />
                    <button onClick={() => setSearchOpen(false)} type="button" className="text-[color:var(--ms-muted)] hover:text-[color:var(--ms-charcoal)]" data-testid="search-close"><X size={16} /></button>
                </form>
            )}

            {/* Mobile menu */}
            {open && (
                <nav className="md:hidden border-t border-[color:var(--ms-border)] px-6 py-5 flex flex-col gap-4 text-sm text-[color:var(--ms-charcoal)] bg-[color:var(--ms-warm-white)] font-medium" data-testid="mobile-menu">
                    {primaryNav.map(l => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="hover:text-[color:var(--ms-sage-dark)]">{l.label}</Link>
                    ))}
                    <Link to="/wishlist" onClick={() => setOpen(false)} className="hover:text-[color:var(--ms-sage-dark)] border-t border-[color:var(--ms-border)] pt-4 mt-2">I Preferiti</Link>
                    <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} className="hover:text-[color:var(--ms-sage-dark)]">{user ? "Il mio account" : "Accedi"}</Link>
                </nav>
            )}
        </header>
    );
}
